import { useTranslation } from 'react-i18next';
import css from 'styled-jsx/css';
import Dialog from 'components/Dialog';
import Button from 'components/Home/SearchByTypeButton';
import MakeModelMenu from 'components/Shared/MakeModelMenu';
import BodyTypeMenu from 'components/BodyTypeMenu';
import PriceMenu from 'components/PriceMenu';
import { colors, screen } from 'components/styles/variables';
import { useGeolocationData } from 'lib/hooks/useGeoLocation';
import WindowLocation from 'lib/WindowLocation';
import Search, { performSearch } from 'lib/Search';
import { SourcePages, Features } from 'lib/EventSchema';
import { FilterEventContext } from 'contexts/FilterEventContext';
import Geocode from 'types/Geocode';
import Make from 'types/Make';
import Model from 'types/Model';

interface TypeSearchProps {
  onMakeModelSearch: (make?: Make, model?: Model, geolocation?: Geocode) => void;
  onBodyTypeSearch: (bodyType: string, geolocation?: Geocode) => void;
  onPriceSearch: (priceMin: number, priceMax: number, geolocation?: Geocode) => void;
  onMakeModelTap?: () => Promise<void>;
  onBodyStyleTap?: () => Promise<void>;
  onPriceTap?: () => Promise<void>;
}

const buttonCSS = css.resolve`
  .search-by-type-button {
    width: 240px;
    height: 48px;
    margin: 0 0 16px 0;
    border: solid 1px ${colors.G1};
    padding: 11px 0 12px 0;
    font-weight: 400;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .search-by-type-button:hover,
  .search-by-type-button:focus {
    background-color: rgba(0, 0, 0, 0.7);
  }

  @media (min-width: ${screen.smMin}) {
    .search-by-type-button {
      width: 192px;
    }

    .first {
      border-radius: 8px 0 0 8px;
      border-right: none;
    }

    .second {
      border-radius: 0;
    }

    .third {
      border-radius: 0 8px 8px 0;
      border-left: none;
    }
  }
  @media (max-height: 667px) {
    .first {
      border-radius: 8px 0 0 8px;
      border-right: none;
    }

    .second {
      border-radius: 0;
    }

    .third {
      border-radius: 0 8px 8px 0;
      border-left: none;
    }
  }
`;

const TypeSearch = ({
  onMakeModelSearch,
  onBodyTypeSearch,
  onPriceSearch,
  onMakeModelTap = (): Promise<void> => Promise.resolve(),
  onBodyStyleTap = (): Promise<void> => Promise.resolve(),
  onPriceTap = (): Promise<void> => Promise.resolve(),
}: TypeSearchProps): JSX.Element => {
  const { t } = useTranslation();
  const geolocation = useGeolocationData();
  const push = WindowLocation.assign;

  const handleMakeModelSearch = (make?: Make, model?: Model): void => {
    onMakeModelSearch(make, model, geolocation);
    const search = new Search({ make, model, location: geolocation });
    performSearch(search, push);
  };

  const handleBodyTypeSearch = (bodyType: string): void => {
    onBodyTypeSearch(bodyType, geolocation);
    const search = new Search({ bodyType, location: geolocation });
    performSearch(search, push);
  };

  const handlePriceMenuSearch = (priceMin: number, priceMax: number): void => {
    onPriceSearch(priceMin, priceMax, geolocation);
    const search = new Search({
      priceMin,
      priceMax,
      location: geolocation,
    });
    performSearch(search, push);
  };

  return (
    <FilterEventContext.Provider
      value={{ sourcePage: SourcePages.Home, feature: Features.legacySearch }}
    >
      <Dialog
        button={({ toggle }): JSX.Element => (
          <Button
            label={t('paidLanding.makeModel')}
            onClick={(): void => {
              toggle();
              onMakeModelTap();
            }}
            className={`search-by-type-button first ${buttonCSS.className}`}
          />
        )}
        content={(): JSX.Element => (
          <MakeModelMenu
            allMakesAndModelsOption={true}
            onSelect={handleMakeModelSearch}
            mobileHeight="full"
          />
        )}
      />
      <Dialog
        button={({ toggle }): JSX.Element => (
          <Button
            onClick={(): void => {
              toggle();
              onBodyStyleTap();
            }}
            label={t('commonlyUsed.bodyStyle')}
            type={'bodyType'}
            className={`search-by-type-button second ${buttonCSS.className}`}
          />
        )}
        content={(): JSX.Element => <BodyTypeMenu onSelect={handleBodyTypeSearch} />}
      />
      <Dialog
        button={({ toggle }): JSX.Element => (
          <Button
            onClick={(): void => {
              toggle();
              onPriceTap();
            }}
            label={t('paidLanding.price')}
            className={`search-by-type-button third ${buttonCSS.className}`}
          />
        )}
        content={(): JSX.Element => <PriceMenu onSubmit={handlePriceMenuSearch} />}
      />
      {buttonCSS.styles}
    </FilterEventContext.Provider>
  );
};

export default TypeSearch;
